import { Cartesian2, BoundingSphere } from "cesium";

// short English comment: reusable edge auto-pan helper for Cesium Viewer
export function createEdgeAutoPan({
  viewer,

  // short English comment: how close to canvas edge triggers pan
  edgePx = 22,

  // short English comment: strength curve range near edge
  strengthMin = 0.15,
  strengthMax = 1.0,

  // short English comment: clamp final speed (m/s)
  speedMin = 5,
  speedMax = 60,

  // short English comment: default speed profile (m/s) by zoom
  speedZGt24 = 5,
  speedZ22_24 = 20,
  speedZLt22 = 50,

  // short English comment: override whole speed computation if you want
  getSpeedMps, // ({ zoom }) => number

  // short English comment: optional override for zoom estimation
  getZoomLevel, // () => number
} = {}) {
  if (!viewer) throw new Error("createEdgeAutoPan: viewer is required");

  const scene = viewer.scene;
  const canvas = scene.canvas;

  const lastMouse = new Cartesian2(-1, -1);
  let rafId = null;
  let lastRafTime = 0;
  let isDestroyed = false;

  const clamp01 = (v) => Math.max(0, Math.min(1, v));
  const lerp = (a, b, t) => a + (b - a) * clamp01(t);

  const edgeStrength = (dPx) => {
    const t = 1 - clamp01(dPx / Math.max(1, edgePx));
    const s = t * t * (3 - 2 * t);
    return strengthMin + (strengthMax - strengthMin) * s;
  };

  const EARTH_RADIUS_M = 6378137;

  const getApproxZoomLevelDefault = () => {
    const camCarto = viewer.camera.positionCartographic;
    if (!camCarto) return 25;

    const lat = Number(camCarto.latitude);
    const cosLat = Number.isFinite(lat) ? Math.max(0.15, Math.abs(Math.cos(lat))) : 1;

    const w = canvas.clientWidth || 1;
    const h = canvas.clientHeight || 1;

    const center = new Cartesian2(w / 2, h / 2);
    const ray = viewer.camera.getPickRay(center);
    const p = ray ? scene.globe.pick(ray, scene) : null;

    if (!p) return 25;

    const bs = new BoundingSphere(p, 1.0);
    const mpp = viewer.camera.getPixelSize(bs, w, h);

    if (!Number.isFinite(mpp) || mpp <= 0) return 25;

    const z = Math.log2((2 * Math.PI * EARTH_RADIUS_M * cosLat) / (256 * mpp));
    return Math.max(0, Math.min(30, z));
  };

  const getZoom = () => {
    try {
      const z = Number(getZoomLevel ? getZoomLevel() : getApproxZoomLevelDefault());
      return Number.isFinite(z) ? z : 25;
    } catch {
      return 25;
    }
  };

  const getSpeedMpsDefault = ({ zoom }) => {
    let v;
    if (zoom >= 24) {
      v = lerp(speedZ22_24, speedZGt24, (Math.min(26, zoom) - 24) / 2);
    } else if (zoom >= 22) {
      v = lerp(speedZLt22, speedZ22_24, (zoom - 22) / 2);
    } else {
      v = speedZLt22;
    }
    return Math.max(speedMin, Math.min(speedMax, v));
  };

  const getSpeed = () => {
    const zoom = getZoom();
    if (typeof getSpeedMps === "function") {
      const v = Number(getSpeedMps({ zoom }));
      if (Number.isFinite(v) && v > 0) return Math.max(speedMin, Math.min(speedMax, v));
      return getSpeedMpsDefault({ zoom });
    }
    return getSpeedMpsDefault({ zoom });
  };

  const panIfNearEdge = (screenPos, dtSec) => {
    if (!screenPos) return;

    const w = canvas.clientWidth || 0;
    const h = canvas.clientHeight || 0;
    if (w <= 2 || h <= 2) return;

    const x = screenPos.x;
    const y = screenPos.y;

    const dLeft = x;
    const dRight = w - 1 - x;
    const dTop = y;
    const dBottom = h - 1 - y;

    let vx = 0;
    let vy = 0;

    let sx = 0;
    let sy = 0;

    if (dLeft >= 0 && dLeft < edgePx) {
      vx = -1;
      sx = edgeStrength(dLeft);
    } else if (dRight >= 0 && dRight < edgePx) {
      vx = +1;
      sx = edgeStrength(dRight);
    }

    if (dTop >= 0 && dTop < edgePx) {
      vy = -1;
      sy = edgeStrength(dTop);
    } else if (dBottom >= 0 && dBottom < edgePx) {
      vy = +1;
      sy = edgeStrength(dBottom);
    }

    if (vx === 0 && vy === 0) return;

    const speedMps = getSpeed();
    const dist = speedMps * Math.max(0.001, dtSec);

    try {
      if (vx !== 0) viewer.camera.moveRight(vx * dist * sx);
      if (vy !== 0) viewer.camera.moveDown(vy * dist * sy);
    } catch {}
  };

  const tick = () => {
    if (isDestroyed) return;
    if (rafId == null) return;

    const now = performance.now();
    const dtSec = Math.max(0.001, (now - lastRafTime) / 1000);
    lastRafTime = now;

    if (lastMouse.x >= 0 && lastMouse.y >= 0) panIfNearEdge(lastMouse, dtSec);

    rafId = requestAnimationFrame(tick);
  };

  const start = () => {
    if (isDestroyed) return;
    if (rafId != null) return;
    lastRafTime = performance.now();
    rafId = requestAnimationFrame(tick);
  };

  const stop = () => {
    if (rafId == null) return;
    try {
      cancelAnimationFrame(rafId);
    } catch {}
    rafId = null;
  };

  const setMousePosition = (pos) => {
    if (!pos) return;

    const w = canvas.clientWidth || 0;
    const h = canvas.clientHeight || 0;

    lastMouse.x = Math.max(0, Math.min(Math.max(0, w - 1), pos.x));
    lastMouse.y = Math.max(0, Math.min(Math.max(0, h - 1), pos.y));
  };

  const destroy = () => {
    if (isDestroyed) return;
    stop();
    isDestroyed = true;
  };

  return { start, stop, setMousePosition, destroy };
}
